import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, Clock3, Gauge, PiggyBank, ShieldCheck, Sparkles } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { Faq } from "@/components/Faq";
import { WhatsIncluded } from "@/components/WhatsIncluded";
import { PreventivoSection } from "@/components/PreventivoSection";
import { MobileStickyBar } from "@/components/MobileStickyBar";
import { UsatoStrip } from "@/components/UsatoStrip";
import { useSeo, faqSchema, SITE_URL } from "@/lib/seo";
import { FAQ_NLT_USATO, CATEGORIE_USATO } from "@/data/usato";

const PATH = "/noleggio-lungo-termine-usato/";

const PUNTI = [
  { icon: Gauge, t: "Massimo 70/80.000 km", d: "Le auto arrivano da un primo ciclo di noleggio a lungo termine, con chilometraggio contenuto e storico di manutenzione completo." },
  { icon: Sparkles, t: "Selezionate una a una", d: "Non tutte rientrano nel circuito: scegliamo solo le migliori e le rimettiamo a disposizione con la formula SecondLife." },
  { icon: ShieldCheck, t: "Stesse garanzie del nuovo", d: "Assicurazioni, manutenzione e assistenza identiche a un noleggio a lungo termine tradizionale: cambia solo l'auto, non il servizio." },
  { icon: PiggyBank, t: "Risparmio fino al 40%", d: "L'auto ha già assorbito la parte più pesante della svalutazione: il canone del noleggio lungo termine usato può costare fino al 40% in meno." },
  { icon: Clock3, t: "Stock aggiornato ogni ora", d: "Le auto che vedi sono disponibili adesso; quelle assegnate escono dal sito automaticamente. Per questo durano pochissimo." },
  { icon: BadgeCheck, t: "Prezzo chiaro, su richiesta", d: "Ogni auto riporta prezzo ed esempio di quotazione appena il consulente li inserisce; altrimenti li chiedi con un clic e ti risponde un consulente XMOVE." },
];

const CONFRONTO = [
  { voce: "Auto", nuovo: "Da ordinare o con consegne medio/lunghe", usato: "Già a stock, con foto e km reali" },
  { voce: "Chilometraggio", nuovo: "Zero km", usato: "Massimo 70/80.000 km, certificati" },
  { voce: "Canone mensile", nuovo: "Prezzo pieno del nuovo", usato: "Fino al 40% in meno" },
  { voce: "Servizi inclusi", nuovo: "RCA, Kasko, manutenzione, assistenza", usato: "Gli stessi, identici" },
  { voce: "Scelta", nuovo: "Configurabile o da stock pronta consegna", usato: "Solo le auto selezionate a stock, aggiornate ogni ora" },
];

const PASSI = [
  { n: "01", t: "Scegli l'auto a stock", d: "Sfoglia il noleggio lungo termine usato per marca, alimentazione, cambio o potenza: ogni scheda ha foto, km, anno e dotazioni." },
  { n: "02", t: "Chiedi la quotazione", d: "Compila il modulo o scrivi su WhatsApp: un consulente XMOVE ti risponde con disponibilità, canone mensile e condizioni." },
  { n: "03", t: "Firma e guida", d: "Stessa procedura del noleggio a lungo termine tradizionale: contratto, consegna e tutti i servizi inclusi nel canone." },
];

export default function NltUsato() {
  useSeo({
    title: "Noleggio Lungo Termine Usato: auto SecondLife fino al 40% in meno | XMOVE Italia",
    description:
      "Noleggio lungo termine usato (NLT Re-Use / SecondLife): auto che hanno concluso un primo ciclo di noleggio, max 70/80.000 km, selezionate una a una, stesse garanzie del nuovo e canone fino al 40% in meno. Stock aggiornato ogni ora.",
    path: PATH,
    jsonLd: [
      faqSchema(FAQ_NLT_USATO),
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Noleggio lungo termine usato", item: `${SITE_URL}${PATH}` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Noleggio lungo termine usato (SecondLife / NLT Re-Use)",
        serviceType: "Noleggio a lungo termine di auto usate",
        provider: { "@type": "Organization", name: "XMOVE Italia", url: SITE_URL },
        areaServed: "IT",
        url: `${SITE_URL}${PATH}`,
        description: "Auto usate provenienti da un primo ciclo di noleggio, massimo 70/80.000 km, con le stesse garanzie del noleggio a lungo termine tradizionale e un canone fino al 40% inferiore.",
      },
    ],
  });

  return (
    <div className="pb-[72px] md:pb-0" data-testid="nlt-usato-page">
      <Header />

      <section className="bg-xmove-night pb-16 pt-32 text-white lg:pb-24 lg:pt-40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <nav aria-label="breadcrumb" className="flex items-center gap-1.5 text-xs text-white/60">
              <Link to="/" className="hover:text-white">Home</Link><span>/</span><span className="text-white">Noleggio lungo termine usato</span>
            </nav>
            <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-xmove-orange">SecondLife · NLT Re-Use</p>
            <h1 className="mt-3 max-w-4xl font-display text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl" data-testid="nlt-usato-h1">
              Noleggio lungo termine usato: stesse garanzie, fino al 40% in meno.
            </h1>
            <p className="mt-5 max-w-2xl text-base text-white/70 md:text-lg">
              Il noleggio lungo termine di auto usate SecondLife è la formula XMOVE per chi vuole un'auto subito disponibile, con pochi
              chilometri e tutti i servizi inclusi, senza pagare il prezzo del nuovo. Lo stock si aggiorna ogni ora.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/usato/" data-testid="nlt-usato-cta-stock" className="inline-flex items-center gap-2 rounded-full bg-xmove-orange px-6 py-3 text-sm font-bold text-white transition-transform hover:scale-[1.02]">
                Vedi le auto a stock <ArrowRight size={16} />
              </Link>
              <a href="#preventivo" className="inline-flex items-center rounded-full border border-white/25 px-6 py-3 text-sm font-bold text-white hover:border-white/60">Chiedi una quotazione</a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-20" data-testid="nlt-usato-cosa">
        <Reveal>
          <div className="grid gap-10 lg:grid-cols-[1.1fr_1fr]">
            <div className="space-y-5 text-base leading-relaxed text-xmove-muted">
              <h2 className="font-display text-3xl font-bold text-xmove-ink">Cos'è il noleggio lungo termine usato</h2>
              <p>
                Il <strong className="text-xmove-ink">noleggio lungo termine usato</strong> (lo chiamiamo SecondLife, o NLT Re-Use) funziona
                esattamente come il noleggio a lungo termine che già conosci: un canone mensile tutto incluso, nessun pensiero per assicurazione,
                manutenzione e assistenza. La differenza è l'auto: invece di un veicolo nuovo da ordinare, guidi una vettura che ha appena
                concluso un primo ciclo di noleggio, con massimo 70/80.000 km.
              </p>
              <p>
                Non tutte le auto rientrano nel circuito. Al termine del primo contratto selezioniamo le migliori — storico di manutenzione
                completo, chilometraggio contenuto, condizioni verificate con foto e video a 360° — e le rimettiamo a disposizione con le stesse
                garanzie di un noleggio a lungo termine tradizionale.
              </p>
              <p>
                Il vantaggio economico è immediato: un'auto già immatricolata ha già assorbito la parte più pesante della svalutazione, quindi il
                canone del noleggio lungo termine usato può costare <strong className="text-xmove-ink">fino al 40% in meno</strong> rispetto alla
                stessa auto nuova, a parità di servizi inclusi.
              </p>
            </div>
            <ul className="grid gap-4 sm:grid-cols-2">
              {PUNTI.map((p) => (
                <li key={p.t} className="rounded-2xl border border-xmove-ink/8 bg-white p-5 shadow-soft">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-xmove-orange/12 text-xmove-orange"><p.icon size={18} /></span>
                  <h3 className="mt-3 font-display text-base font-bold text-xmove-ink">{p.t}</h3>
                  <p className="mt-1.5 text-sm text-xmove-muted">{p.d}</p>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </section>

      <section className="bg-xmove-cream py-16 lg:py-20" data-testid="nlt-usato-confronto">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-widest text-xmove-orange">Nuovo o SecondLife?</p>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold text-xmove-ink sm:text-4xl">Noleggio lungo termine nuovo e usato a confronto</h2>
            <div className="mt-10 overflow-hidden rounded-2xl border border-xmove-ink/8 bg-white shadow-soft">
              <div className="hidden grid-cols-3 gap-4 border-b border-xmove-ink/8 bg-xmove-cream px-6 py-4 text-xs font-bold uppercase tracking-wider text-xmove-muted sm:grid">
                <span>Voce</span><span>NLT auto nuova</span><span className="text-xmove-orange">NLT usato SecondLife</span>
              </div>
              {CONFRONTO.map((r) => (
                <div key={r.voce} className="grid grid-cols-1 gap-2 border-b border-xmove-ink/6 px-5 py-4 last:border-0 sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
                  <span className="text-sm font-bold text-xmove-ink">{r.voce}</span>
                  <span className="text-sm text-xmove-muted"><span className="mb-0.5 block text-[10px] font-bold uppercase tracking-wider text-xmove-muted/70 sm:hidden">NLT nuovo</span>{r.nuovo}</span>
                  <span className="text-sm font-semibold text-xmove-ink"><span className="mb-0.5 block text-[10px] font-bold uppercase tracking-wider text-xmove-orange/80 sm:hidden">NLT usato SecondLife</span>{r.usato}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-20" data-testid="nlt-usato-passi">
        <Reveal>
          <h2 className="font-display text-3xl font-bold text-xmove-ink sm:text-4xl">Come funziona, in tre passi</h2>
          <ol className="mt-10 grid gap-6 md:grid-cols-3">
            {PASSI.map((p) => (
              <li key={p.n} className="rounded-2xl border border-xmove-ink/8 bg-white p-6 shadow-soft">
                <span className="font-display text-3xl font-bold text-xmove-orange">{p.n}</span>
                <h3 className="mt-3 font-display text-lg font-bold text-xmove-ink">{p.t}</h3>
                <p className="mt-2 text-sm text-xmove-muted">{p.d}</p>
              </li>
            ))}
          </ol>
          <div className="mt-10 flex flex-wrap gap-2" data-testid="nlt-usato-categorie">
            <span className="mr-2 self-center text-sm font-bold text-xmove-ink">Sfoglia il noleggio lungo termine usato:</span>
            {Object.entries(CATEGORIE_USATO).map(([k, c]) => (
              <Link key={k} to={`/usato/categoria/${k}/`} className="rounded-full border border-xmove-ink/10 px-3 py-1.5 text-xs font-semibold text-xmove-ink transition-colors hover:border-xmove-orange hover:text-xmove-orange">{c.nome}</Link>
            ))}
            <Link to="/usato/" className="rounded-full bg-xmove-night px-3 py-1.5 text-xs font-semibold text-white hover:bg-xmove-orange">Tutto lo stock</Link>
          </div>
        </Reveal>
      </section>

      <WhatsIncluded dark />
      <UsatoStrip limit={3} titolo="Noleggio lungo termine usato: gli ultimi arrivi" sottotitolo="Auto appena rientrate dal primo ciclo di noleggio e già selezionate per la formula SecondLife." testid="nlt-usato-strip" />
      <Faq faqs={FAQ_NLT_USATO} title="Domande frequenti sul noleggio lungo termine usato" />
      <PreventivoSection idPrefix="nlt-usato" title="Vuoi una quotazione sul noleggio lungo termine usato?" subtitle="Dicci quale auto ti interessa o che budget hai: ti rispondiamo con le auto SecondLife disponibili adesso." />
      <Footer />
      <MobileStickyBar />
    </div>
  );
}
